import { useMemo, useState } from "react";
import {
  Clock,
  Copy,
  Edit,
  ExternalLink,
  Heart,
  MapPin,
  Phone,
  PlusCircle,
  Trash2,
  Utensils,
  X,
} from "lucide-react";

export type SnackSpot = {
  id: number;
  userId: number;
  label: string;
  phone?: string | null;
  deliveryUrl?: string | null;
  locationHint?: string | null;
  notes?: string | null;
  favorite?: number | boolean | null;
  lastOrderedAt?: string | null;
  created_at: string;
  updated_at: string;
};

export type SnackSpotDraft = {
  label: string;
  phone?: string;
  deliveryUrl?: string;
  locationHint?: string;
  notes?: string;
  favorite?: boolean;
};

interface LateNightSnackPanelProps {
  spots: SnackSpot[];
  loading?: boolean;
  onCreate: (payload: SnackSpotDraft) => Promise<void>;
  onUpdate: (id: number, payload: Partial<SnackSpot>) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onMarkOrdered: (id: number) => Promise<void>;
  onOrderClick?: (restaurantName: string, itemName: string, url?: string) => void;
}

const defaultFormState: SnackSpotDraft = {
  label: "",
  phone: "",
  deliveryUrl: "",
  locationHint: "",
  notes: "",
  favorite: true,
};

const LateNightSnackPanel = ({
  spots,
  loading,
  onCreate,
  onUpdate,
  onDelete,
  onMarkOrdered,
  onOrderClick,
}: LateNightSnackPanelProps) => {
  const [formState, setFormState] = useState<SnackSpotDraft>(defaultFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editFormState, setEditFormState] = useState<Partial<SnackSpot>>({});
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedSpotForCall, setSelectedSpotForCall] = useState<SnackSpot | null>(null);

  const sortedSpots = useMemo(() => {
    return [...spots].sort((a, b) => {
      const favDiff = Number(Boolean(b.favorite)) - Number(Boolean(a.favorite));
      if (favDiff !== 0) return favDiff;
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
  }, [spots]);

  const lastOrderedLabel = (value?: string | null) => {
    if (!value) return "Not yet ordered";
    try {
      const date = new Date(value);
      return `Last ordered ${date.toLocaleDateString()} · ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } catch {
      return value;
    }
  };

  const handleInputChange = (field: keyof SnackSpotDraft, value: string | boolean) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formState.label.trim()) {
      setFormError("Give your spot a name");
      return;
    }
    setFormError(null);
    setCreating(true);
    try {
      await onCreate({
        label: formState.label.trim(),
        phone: formState.phone?.trim() || undefined,
        deliveryUrl: formState.deliveryUrl?.trim() || undefined,
        locationHint: formState.locationHint?.trim() || undefined,
        notes: formState.notes?.trim() || undefined,
        favorite: Boolean(formState.favorite),
      });
      setFormState(defaultFormState);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Unable to save spot");
    } finally {
      setCreating(false);
    }
  };

  const handleFavoriteToggle = async (spot: SnackSpot) => {
    setPendingId(spot.id);
    try {
      await onUpdate(spot.id, { favorite: spot.favorite ? 0 : 1 });
    } finally {
      setPendingId(null);
    }
  };

  const handleMarkOrdered = async (spotId: number) => {
    setPendingId(spotId);
    try {
      await onMarkOrdered(spotId);
    } finally {
      setPendingId(null);
    }
  };

  const handleDelete = async (spotId: number) => {
    setPendingId(spotId);
    try {
      await onDelete(spotId);
      setEditingId(null); // Close edit mode after deletion
    } finally {
      setPendingId(null);
    }
  };

  const handleStartEdit = (spot: SnackSpot) => {
    setEditingId(spot.id);
    setEditFormState({
      label: spot.label,
      phone: spot.phone || '',
      deliveryUrl: spot.deliveryUrl || '',
      locationHint: spot.locationHint || '',
      notes: spot.notes || '',
      favorite: Boolean(spot.favorite),
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormState({});
  };

  const handleSaveEdit = async (spotId: number) => {
    if (!editFormState.label?.trim()) {
      alert('Spot name is required');
      return;
    }
    setPendingId(spotId);
    try {
      await onUpdate(spotId, editFormState);
      setEditingId(null);
      setEditFormState({});
    } finally {
      setPendingId(null);
    }
  };

  const handleEditInputChange = (field: string, value: string | boolean) => {
    setEditFormState(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCopyNotes = async (notes?: string | null) => {
    if (!notes) return;
    try {
      await navigator.clipboard.writeText(notes);
    } catch {
      // ignore clipboard failures
    }
  };

  return (
    <div className="space-y-5 text-white">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-purple-200">
          <Utensils className="w-4 h-4" /> Late-night speed dial
        </div>
        <div>
          <h2 className="text-2xl font-semibold">Snack Hotline</h2>
          <p className="text-sm text-white/70">
            Pin the bodegas, diners, and 24-hour gems you trust so you can order in two taps.
          </p>
        </div>
      </header>

      <form
        onSubmit={handleSubmit}
        className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3"
      >
        <div className="grid gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs text-white/70">
            <span className="uppercase tracking-[0.25em] text-white/50">Spot name *</span>
            <input
              value={formState.label}
              onChange={(e) => handleInputChange("label", e.target.value)}
              placeholder="7-Eleven on Sunset"
              className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-white/70">
            <span className="uppercase tracking-[0.25em] text-white/50">Phone</span>
            <input
              value={formState.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              placeholder="(555) 123-9876"
              className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-white/70">
            <span className="uppercase tracking-[0.25em] text-white/50">Ordering link</span>
            <input
              value={formState.deliveryUrl}
              onChange={(e) => handleInputChange("deliveryUrl", e.target.value)}
              placeholder="https://doordash.com/..."
              className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-white/70">
            <span className="uppercase tracking-[0.25em] text-white/50">Location hint</span>
            <input
              value={formState.locationHint}
              onChange={(e) => handleInputChange("locationHint", e.target.value)}
              placeholder="By campus gate / delivers until 2am"
              className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
            />
          </label>
        </div>
        <label className="flex flex-col gap-1 text-xs text-white/70">
          <span className="uppercase tracking-[0.25em] text-white/50">Notes</span>
          <textarea
            value={formState.notes}
            onChange={(e) => handleInputChange("notes", e.target.value)}
            placeholder="Ask for extra chili oil; nachos need guac on the side."
            className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none min-h-[80px]"
          />
        </label>
        {formError && <p className="text-sm text-rose-300">{formError}</p>}
        <div className="flex items-center justify-between text-xs text-white/70 flex-wrap gap-2">
          <label className="inline-flex items-center gap-2 text-white/80 group cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(formState.favorite)}
              onChange={(e) => handleInputChange("favorite", e.target.checked)}
              className="accent-purple-500"
            />
            <span className="relative">
              Pin to top of the list
              <span className="absolute -bottom-6 left-0 w-64 p-2 bg-black/90 border border-purple-400/30 rounded-lg text-xs text-white/80 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal">
                💡 Pinned spots appear at the top of your list so you can access your favorites quickly
              </span>
            </span>
          </label>
          <button
            type="submit"
            disabled={creating}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 border text-sm font-semibold transition ${
              creating
                ? "opacity-50 cursor-not-allowed border-white/20 text-white/50"
                : "border-purple-400 text-purple-100 hover:bg-purple-500/20"
            }`}
          >
            <PlusCircle className="w-4 h-4" />
            Save spot
          </button>
        </div>
      </form>

      <section className="space-y-3">
        <div className="flex items-center justify-between text-xs uppercase tracking-[0.25em] text-white/60">
          <span>Favorites & go-to spots</span>
          {loading && <span className="text-white/40">Syncing…</span>}
        </div>
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
          {sortedSpots.length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 p-6 text-center text-sm text-white/60">
              Start a personal snack rolodex. Add your first spot above.
            </div>
          )}
          {sortedSpots.map((spot) => {
            const isPending = pendingId === spot.id;
            const isEditing = editingId === spot.id;
            return (
              <article
                key={spot.id}
                className={`rounded-2xl border p-4 space-y-3 ${
                  isEditing 
                    ? 'border-blue-400/40 bg-blue-900/10' 
                    : 'border-white/10 bg-black/20'
                }`}
              >
                {!isEditing ? (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-white">{spot.label}</h3>
                          <button
                            type="button"
                            onClick={() => handleFavoriteToggle(spot)}
                            className="text-rose-200 hover:text-rose-100"
                            title={spot.favorite ? "Unpin from top" : "Pin to top of list"}
                            disabled={isPending}
                          >
                            <Heart
                              className={`w-4 h-4 ${spot.favorite ? "fill-current" : ""}`}
                            />
                          </button>
                        </div>
                        <p className="text-xs text-white/50 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {spot.locationHint ? spot.locationHint : "Add a location hint"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-white/70">
                        <button
                          type="button"
                          onClick={() => handleMarkOrdered(spot.id)}
                          disabled={isPending}
                          className="px-3 py-1 rounded-full border border-white/20 hover:bg-white/10"
                          title="Mark as ordered"
                        >
                          <Clock className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleStartEdit(spot)}
                          disabled={isPending}
                          className="px-3 py-1 rounded-full border border-blue-400/40 hover:bg-blue-500/10 text-blue-200"
                          title="Edit or delete"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm">
                      {spot.deliveryUrl && (
                        <button
                          onClick={() => {
                            const url = spot.deliveryUrl.startsWith('http') ? spot.deliveryUrl : `https://${spot.deliveryUrl}`;
                            window.open(url, '_blank');
                            if (onOrderClick) {
                              onOrderClick(spot.label, spot.notes || 'Order', url);
                            }
                          }}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-sky-400/40 text-sky-100 hover:bg-sky-500/10 font-medium"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Order Online
                        </button>
                      )}
                      {spot.phone && (
                        <button
                          onClick={() => {
                            setSelectedSpotForCall(spot);
                            setShowCallModal(true);
                          }}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-400/40 text-emerald-100 hover:bg-emerald-500/10"
                        >
                          <Phone className="w-3.5 h-3.5" />
                          Call
                        </button>
                      )}
                      {spot.notes && (
                        <button
                          type="button"
                          onClick={() => handleCopyNotes(spot.notes)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 text-white/80 hover:bg-white/10"
                        >
                          <Copy className="w-3.5 h-3.5" />
                          Copy notes
                        </button>
                      )}
                    </div>
                    {spot.notes && (
                      <p className="text-sm text-white/80 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
                        {spot.notes}
                      </p>
                    )}
                    <p className="text-xs text-white/50">{lastOrderedLabel(spot.lastOrderedAt)}</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-blue-200">Editing Spot</h4>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleSaveEdit(spot.id)}
                          disabled={isPending}
                          className="px-3 py-1 rounded-full border border-emerald-400/40 hover:bg-emerald-500/10 text-emerald-200 text-xs"
                        >
                          Save Changes
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="px-3 py-1 rounded-full border border-white/20 hover:bg-white/10 text-xs"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <label className="flex flex-col gap-1 text-xs text-white/70">
                        <span className="uppercase tracking-[0.25em] text-white/50">Spot name *</span>
                        <input
                          value={editFormState.label || ''}
                          onChange={(e) => handleEditInputChange('label', e.target.value)}
                          className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-xs text-white/70">
                        <span className="uppercase tracking-[0.25em] text-white/50">Phone</span>
                        <input
                          value={editFormState.phone || ''}
                          onChange={(e) => handleEditInputChange('phone', e.target.value)}
                          className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-xs text-white/70">
                        <span className="uppercase tracking-[0.25em] text-white/50">Ordering link</span>
                        <input
                          value={editFormState.deliveryUrl || ''}
                          onChange={(e) => handleEditInputChange('deliveryUrl', e.target.value)}
                          className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-xs text-white/70">
                        <span className="uppercase tracking-[0.25em] text-white/50">Location hint</span>
                        <input
                          value={editFormState.locationHint || ''}
                          onChange={(e) => handleEditInputChange('locationHint', e.target.value)}
                          className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                        />
                      </label>
                    </div>
                    <label className="flex flex-col gap-1 text-xs text-white/70">
                      <span className="uppercase tracking-[0.25em] text-white/50">Notes</span>
                      <textarea
                        value={editFormState.notes || ''}
                        onChange={(e) => handleEditInputChange('notes', e.target.value)}
                        className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400 min-h-[80px]"
                      />
                    </label>
                    <div className="flex items-center justify-between pt-2">
                      <label className="inline-flex items-center gap-2 text-white/80 text-xs">
                        <input
                          type="checkbox"
                          checked={Boolean(editFormState.favorite)}
                          onChange={(e) => handleEditInputChange('favorite', e.target.checked)}
                          className="accent-purple-500"
                        />
                        Pin to top of list
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Delete "${spot.label}"?`)) {
                            handleDelete(spot.id);
                          }
                        }}
                        disabled={isPending}
                        className="px-3 py-1 rounded-full border border-rose-400/40 hover:bg-rose-500/10 text-rose-200 text-xs"
                      >
                        <Trash2 className="w-3.5 h-3.5 inline mr-1" />
                        Delete Spot
                      </button>
                    </div>
                  </>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {/* Call Modal with Auto-Mute */}
      {showCallModal && selectedSpotForCall && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl max-w-md w-full shadow-2xl border border-slate-700">
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Phone className="w-6 h-6 text-emerald-400" />
                    Make a Call
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">{selectedSpotForCall.label}</p>
                </div>
                <button
                  onClick={() => {
                    setShowCallModal(false);
                    setSelectedSpotForCall(null);
                  }}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-2">
                <p className="text-sm text-amber-200 font-medium">🔇 Privacy Protection</p>
                <p className="text-xs text-amber-100/80">
                  When you make this call, we'll automatically:
                </p>
                <ul className="text-xs text-amber-100/80 space-y-1 ml-4">
                  <li>• <strong>Mute your microphone</strong> in study sessions</li>
                  <li>• <strong>Turn off your video</strong> (shows avatar)</li>
                  <li>• Display <em>"📞 On a quick call - BRB!"</em> to study buddies</li>
                </ul>
                <p className="text-xs text-amber-100/80 mt-2">
                  After your call, click <strong>"End Call & Rejoin"</strong> to restore audio/video.
                </p>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <a
                  href={`tel:${selectedSpotForCall.phone}`}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
                  onClick={() => {
                    // Store call state for virtual room to detect
                    sessionStorage.setItem('whipt_on_call', 'true');
                    sessionStorage.setItem('whipt_call_start', new Date().toISOString());
                    sessionStorage.setItem('whipt_call_spot', selectedSpotForCall.label);
                    
                    // Trigger pending order for ETA modal after call
                    if (onOrderClick) {
                      onOrderClick(selectedSpotForCall.label, selectedSpotForCall.notes || 'Phone Order', undefined);
                    }
                    
                    setShowCallModal(false);
                    setSelectedSpotForCall(null);
                  }}
                >
                  <Phone className="w-5 h-5" />
                  Call Now
                </a>
                <button
                  onClick={() => {
                    setShowCallModal(false);
                    setSelectedSpotForCall(null);
                  }}
                  className="px-4 py-3 text-slate-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>

              <p className="text-xs text-center text-slate-500">
                Tip: Use "Order Online" above to skip calls entirely!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LateNightSnackPanel;
